import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, TemplateRef } from '@angular/core';
import { AppState } from '../../../core/app-state';
import { FesBaseComponent } from '../../../shared/components/fes-base.component';
import { ScoutPath, ScoutPoint } from '../../../core/models';
import { ScoutPointPhotoDoc } from '../../../core/schema/scout-point-photo-schema';
import { Subscription, timer } from 'rxjs';
import { ScoutPointPhotoService } from '../../../core/services';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';

export class ScoutPhotoGroupDef {
  name: 'measurement'|'wide-angle'|'point-to-point'|'additional'
  title: string
  requiredCnt: number
}
export class ScoutPointPhotoGroupSelected {
  group: ScoutPhotoGroupDef
  task : ScoutTaskDoc
  path : ScoutPath
  point : ScoutPoint
  photos: ScoutPointPhotoDoc[]
  photoGroup: { title: string, photos: ScoutPointPhotoDoc[] }
}

export class ScoutPointPhotoSelected extends ScoutPointPhotoGroupSelected {
  photo: ScoutPointPhotoDoc
};

export type ContextByPhotoId<ContextType=any> = {
  [scoutPhotoId:string]: ContextType
};

export const defaultBindContextFn = async (photos: ScoutPointPhotoDoc[], contextByPhotoId: {[id:string]:{iconCssClass:string}}) => {
  for( const photo of photos ) {
    const context = {
      iconCssClass: photo?.uploadDate?'fa-check text-success':(photo?.fileName?'fa-circle text-warning':'fa-times text-info')
    };
    //console.log('defaultBindContextFn',photo._id,context);
    contextByPhotoId[photo._id] = context;
  }
};

@Component({
  selector: 'app-scout-point-photo-browser',
  templateUrl: './scout-point-photo-browser.component.html',
  styleUrls: ['./scout-point-photo-browser.component.scss']
})
export class ScoutPointPhotoBrowserComponent extends FesBaseComponent implements OnInit, OnDestroy {
  _scoutPoint: ScoutPoint;

  @Input() scoutTask : ScoutTaskDoc

  @Input() scoutPath : ScoutPath

  @Input() set scoutPoint ( sp: ScoutPoint ) {
    this._scoutPoint = sp
    this.initPhotoWatch()
  }

  @Input() locked : boolean = false
  @Input() newPhotoFn : (groupName:string) => ScoutPointPhotoDoc = null

  @Input() groupSelectEnabled = false

  @Output() photoSelected = new EventEmitter<ScoutPointPhotoSelected>()
  @Output() photoGroupSelected = new EventEmitter<ScoutPointPhotoGroupSelected>()
  @Output() contextBound = new EventEmitter<{ [scoutPhotoId:string]: any} >()

  get scoutPoint() {
    return this._scoutPoint
  }

  readonly PhotoGroupDefs : ScoutPhotoGroupDef[] = [
    { name: 'measurement', 'title': 'Meas', requiredCnt: 4 },
    { name: 'wide-angle', 'title': 'Wide', requiredCnt: 4 },
    { name: 'point-to-point', 'title': 'Path', requiredCnt: 0 },
    { name: 'additional', 'title': 'Addl', requiredCnt: 0 }
  ]

  photoQuerySubscription: Subscription
  isLoadingPhotos = true
  photos: ScoutPointPhotoDoc[] = []
  contextByPhotoId: { [scoutPhotoId:string]: any} = {};
  photosByGroup : { [param: string]: { title: string, photos: ScoutPointPhotoDoc[] } } = {}

  constructor(appState : AppState,
      protected photoService: ScoutPointPhotoService) {
    super(appState)

    this.newPhotoFn = this.onNewPhoto // Note: scout ref modal assigns a different newPhotoFn
  }


  ngOnInit() {
  }


  ngOnDestroy(): void {
    this.destroy()
  }


  @Input() bindContextFn = defaultBindContextFn;


  initPhotoWatch() {
    this.photoQuerySubscription?.unsubscribe()
    this.isLoadingPhotos = true
    this.photos = []
    this.photosByGroup = {}
    this.contextByPhotoId = {};
    if ( this.scoutPoint ) {
      let photoQuery = this.photoService.rxCollection.find().where('scoutPointId').eq(this.scoutPoint.id)
      this.photoQuerySubscription = this.subscribe( photoQuery.$, async (docs : ScoutPointPhotoDoc[]) => {
        this.isLoadingPhotos = false
        this.photoService.initPhotosForPointType(docs,this.scoutPoint.type,this.photos)
        const nowTime = new Date().getTime()
        this.photos.forEach(photo=>{
          try{
            if (photo.modifiedDate) {
              const daysOld= (nowTime-new Date(photo.modifiedDate).getTime())/(1000*3600*24);
              const bgAlpha = Math.pow((daysOld <= 7) ? (1-daysOld/7) : 0,4);
              photo['bgAlpha'] = bgAlpha
            }
          } catch ( error ) {}
        })
        this.initPhotoGroups()
        if ( this.bindContextFn ) {
          await this.bindContextFn(this.photos,this.contextByPhotoId);
        } else {
          await defaultBindContextFn(this.photos, this.contextByPhotoId);
        }
        this.contextBound.next(this.contextByPhotoId);
      },
      error => {
        console.log('photoQuery subscription received error')
        this.isLoadingPhotos = false; this.photoService.publishError(error,this)
      })
    }
  }


  initPhotoGroups() {
    this.photosByGroup = {}

    this.PhotoGroupDefs.forEach( grp => {
      const sortByOrderIndex = (a,b)=> a.groupOrderIndex - b.groupOrderIndex
      let grpPhotoDocs = this.photos.filter(p=>p.group==grp.name).sort(sortByOrderIndex)
      this.photosByGroup[grp.name] = {
        title: grp.name,
        photos: grpPhotoDocs
      }
    })
  }


  onPhotoIconClick = ( photo : ScoutPointPhotoDoc, group: ScoutPhotoGroupDef) => {
    this.photoSelected.emit( {
        photo,
        group,
        task: this.scoutTask,
        path: this.scoutPath,
        point: this.scoutPoint,
        photos: this.photos,
        photoGroup: this.photosByGroup[photo.group]
    } )
  }


  @Output() photoIconDblClick = new EventEmitter<{photo : ScoutPointPhotoDoc, grpDef: ScoutPhotoGroupDef, contextByPhotoId: ContextByPhotoId}>();

  @Input() photoBtnContentTmplRef: TemplateRef<{photo : ScoutPointPhotoDoc, grpDef: ScoutPhotoGroupDef, contextByPhotoId: ContextByPhotoId}>;

  onPhotoGroupClick( grpDef: ScoutPhotoGroupDef ){
    if ( this.groupSelectEnabled ) {
      this.photoGroupSelected.emit( {
        group: grpDef,
        task: this.scoutTask,
        path: this.scoutPath,
        point: this.scoutPoint,
        photos: this.photos,
        photoGroup: this.photosByGroup[grpDef.name]
      })
    }
  }


  createGroupPhoto( group: ScoutPhotoGroupDef ) {
    if ( this.newPhotoFn ) {
      const photoDoc = this.newPhotoFn(group.name)
      this.addPhoto(photoDoc) //adding here because onPhotoIconClick may run before the photoAdded event is emitted
      timer(0).subscribe(_=>{
        this.onPhotoIconClick(photoDoc,group)
      })
    }
  }


  addPhoto( photoDoc: ScoutPointPhotoDoc ) {
    if ( !photoDoc ) {
      console.warn('Attempting to add null photoDoc to photo browswer')
    } else if ( this.photos.some(p=>p==photoDoc)) {
      //console.warn('Photo browser already has photo ' + photoDoc._id)
    } else {
      this.photos.push(photoDoc)
      this.initPhotoGroups()
    }
  }


  onNewPhoto = ( groupName : string ) => {
    console.log('onNewPhoto(' + groupName + ')')
    let photo = this.photoService.rxCollection.newDocument()
    photo.group = groupName
    const groupPhotos = this.photos.filter(doc=>doc.group==groupName)
    const maxOrderIndex = Math.max(0,...groupPhotos.map(doc=>doc.groupOrderIndex))
    photo.groupOrderIndex = maxOrderIndex + 1
    photo.pointType = this.scoutPoint.type
    photo.scoutPointId = this._scoutPoint.id
    this.photos.push(photo)
    return photo
  }
}
